import { Link } from 'react-router-dom';

import styled, { css, DefaultTheme } from 'styled-components';

import fonts from 'apps/promo/theme/fonts';
import media from 'apps/promo/theme/media';

export type ButtonVariant = 'primary' | 'secondary' | 'secondary_base' | 'secondary_light' | 'tertiary' | 'error' | 'danger' | 'hidden' | 'row';

export type ButtonSize = 'tight' | 'fit' | 'small';

interface ButtonStyleProps {
    variant?: ButtonVariant;
    size?: ButtonSize;
    disabled?: boolean;
    isInverted?: boolean;
    raised?: string | boolean;
    negativeMargin?: boolean;
    textCenter?: boolean;
    hidden?: boolean;
}

type ButtonTheme = DefaultTheme & {
    purple_40_tr: string;
    purple_100: string;
    purple_40_tr_base: string;
    purple_100_base: string;
    white_base: string;
    grey_40_lc: string;
    black: string;
    pink_40_tr: string;
    pink_100: string;
    disabled_button_color: string;
    white_24_tr_base: string;
    purple_24_lc: string;
    pink_40_lc: string;
    pink_72_lc: string;
    white: string;
    purple_8_lc: string;
    purple_8_lc_base: string;
    grey_8_lc: string;
    pink_8_tr: string;
    purple_8_tr: string;
    purple_72_lc: string;
    purple_16_tr: string;
    pink_16_tr: string;
    shadow_grey: string;
    white_40_tr_base: string;
};

const getTheme = (props: { theme: DefaultTheme }): ButtonTheme => props.theme as ButtonTheme;
const shouldForwardButtonProp = (prop: string) => !['variant', 'size', 'isInverted', 'raised', 'negativeMargin', 'textCenter', 'hidden'].includes(prop);

const getColor = (props: ButtonStyleProps & { theme: DefaultTheme }) => {
    const theme = getTheme(props);
    switch (props.variant) {
        case 'secondary':
            return props.disabled ? theme.purple_40_tr : theme.purple_100;
        case 'secondary_base':
            return props.disabled ? theme.purple_40_tr_base : theme.purple_100_base;
        case 'secondary_light':
            return props.disabled ? theme.purple_40_tr_base : theme.purple_100_base;
        case 'tertiary':
            return props.isInverted ? theme.white_base : props.disabled && !props.isInverted ? theme.grey_40_lc : theme.black;
        case 'error':
            return props.disabled ? theme.pink_40_tr : theme.pink_100;
        case 'row':
            return props.disabled ? theme.purple_40_tr : theme.purple_100;
        default:
            return props.disabled ? theme.disabled_button_color : theme.white_base;
    }
};

const getVisitedLinkColor = (props: ButtonStyleProps & { theme: DefaultTheme }) => {
    const theme = getTheme(props);
    switch (props.variant) {
        case 'secondary':
            return props.disabled ? theme.purple_40_tr : theme.purple_100;
        case 'secondary_base':
            return props.disabled ? theme.purple_40_tr_base : theme.purple_100_base;
        case 'secondary_light':
            return props.disabled ? theme.purple_40_tr_base : theme.purple_100_base;
        case 'tertiary':
            return props.isInverted ? theme.white_base : props.disabled && !props.isInverted ? theme.grey_40_lc : theme.black;
        case 'error':
            return props.disabled ? theme.pink_40_tr : theme.pink_100;
        case 'row':
            return props.disabled ? theme.purple_40_tr : theme.purple_100;
        default:
            return props.disabled ? theme.white_24_tr_base : theme.white_base;
    }
};

const getBackground = (props: ButtonStyleProps & { theme: DefaultTheme }) => {
    const theme = getTheme(props);
    if (props.variant === 'secondary_base') {
        return theme.white_base;
    }
    if (props.variant === 'secondary_light') {
        return theme.purple_8_tr;
    }
    if (props.variant === 'primary' || !props.variant) {
        return props.disabled ? theme.purple_24_lc : theme.purple_100;
    }
    if (props.variant === 'danger') {
        return props.disabled ? theme.pink_40_lc : theme.pink_100;
    }
    if (props.isInverted) {
        return 'transparent';
    }
    return theme.white;
};

const getHoveredBackground = (props: ButtonStyleProps & { theme: DefaultTheme }) => {
    const theme = getTheme(props);
    switch (props.variant) {
        case 'secondary':
            return props.disabled ? theme.white : theme.purple_8_lc;
        case 'secondary_base':
            return props.disabled ? theme.white_base : theme.purple_8_lc_base;
        case 'secondary_light':
            return theme.purple_16_tr;
        case 'tertiary':
            return props.isInverted ? 'transparent' : props.disabled && !props.isInverted ? theme.white : theme.grey_8_lc;
        case 'error':
            return props.disabled ? theme.white : theme.pink_8_tr;
        case 'danger':
            return props.disabled ? theme.pink_40_lc : theme.pink_72_lc;
        case 'row':
            return props.disabled ? theme.white : theme.purple_8_tr;
        default:
            return props.disabled ? theme.purple_24_lc : theme.purple_72_lc;
    }
};

const getBoxShadow = (props: ButtonStyleProps & { theme: DefaultTheme }) => {
    const theme = getTheme(props);
    const raised = props.raised && !props.disabled;
    if (props.variant === 'primary' || !props.variant) {
        return raised ? `0px 8px 8px ${theme.purple_16_tr}` : 'none';
    }
    if (props.variant === 'danger') {
        return raised ? `0 8px 24px ${theme.pink_16_tr}` : 'none';
    }
    return raised ? `${theme.shadow_grey}` : 'none';
};

const getMobilePadding = (props: ButtonStyleProps) => {
    if (props.variant === 'row') {
        return props.size === 'tight' ? '16px 24px' : '24px';
    }
    switch (props.size) {
        case 'small':
            return '2px 16px';
        case 'tight':
            return '8px 16px';
        default:
            return '16px 32px';
    }
};
const getDesktopPadding = (props: ButtonStyleProps) => {
    if (props.variant === 'row') {
        return props.size === 'tight' ? '20px 32px' : '32px';
    }
    switch (props.size) {
        case 'small':
            return '2px 16px';
        case 'tight':
            return '12px 24px';
        default:
            return '20px 40px';
    }
};
const getMobileRadius = (props: ButtonStyleProps) => {
    if (props.variant === 'row') {
        return '0';
    }
    return props.size === 'small' ? '20px' : props.size === 'tight' ? '32px' : '56px';
};
const getDesktopRadius = (props: ButtonStyleProps) => {
    if (props.variant === 'row') {
        return '0';
    }
    return props.size === 'small' ? '20px' : props.size === 'tight' ? '56px' : '72px';
};
const getMobileWidth = (props: ButtonStyleProps) => {
    return props.variant === 'row' && props.negativeMargin ? 'calc(100% + 48px)' : props.variant === 'row' ? '100%' : 'auto';
};
const getDesktopWidth = (props: ButtonStyleProps) => {
    return props.variant === 'row' && props.negativeMargin ? 'calc(100% + 64px)' : props.variant === 'row' ? '100%' : 'auto';
};

const CommonCss = css<ButtonStyleProps>`
    font-family: ${fonts.gtStandardMMedium};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    border: none;
    width: ${(props) => getMobileWidth(props)};
    box-sizing: border-box;
    display: ${(props) => (props.variant === 'hidden' ? 'none' : 'inline-block')};
    outline: none;
    text-align: ${(props) => (props.variant === 'row' && !props.textCenter ? 'left' : 'center')};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    position: relative;
    vertical-align: top;
    cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
    pointer-events: auto;
    font-size: ${(props) => (props.size === 'small' ? '14px' : '17px')};
    line-height: ${(props) => (props.size === 'small' ? '20px' : '24px')};
    border-radius: ${(props) => getMobileRadius(props)};
    padding: ${(props) => getMobilePadding(props)};
    color: ${(props) => getColor(props)};
    background: ${(props) => getBackground(props)};
    box-shadow: ${(props) => getBoxShadow(props)};
    visibility: ${(props) => (props.hidden ? 'hidden' : 'visible')};
    margin: ${(props) => (props.negativeMargin ? '0 -24px' : null)};

    ${media.desktop1920} {
        font-size: ${(props) => (props.size === 'small' ? '17px' : '20px')};
        line-height: ${(props) => (props.size === 'small' ? '24px' : '32px')};
        border-radius: ${(props) => getDesktopRadius(props)};
        padding: ${(props) => getDesktopPadding(props)};
        margin: ${(props) => (props.negativeMargin ? '0 -32px' : null)};
        width: ${(props) => getDesktopWidth(props)};
    }

    &:link,
    &:visited,
    &:focus {
        color: ${(props) => getVisitedLinkColor(props)};
    }

    &:hover,
    &:active,
    &:focus {
        background: ${(props) => getHoveredBackground(props)};
        color: ${(props) => (props.variant === 'tertiary' && props.isInverted ? getTheme(props).white_40_tr_base : null)};
    }
`;

export const CommonButton = styled.button.withConfig({ shouldForwardProp: shouldForwardButtonProp })<ButtonStyleProps>`
    ${CommonCss}
`;

export const LinkButton = styled.a.withConfig({ shouldForwardProp: shouldForwardButtonProp })<ButtonStyleProps>`
    ${CommonCss}
`;

export const ReactLinkButton = styled(Link).withConfig({ shouldForwardProp: shouldForwardButtonProp })<ButtonStyleProps>`
    ${CommonCss}
`;

export const IconContainer = styled.span`
    position: relative;
    top: 1px;
`;
